interface StateChangeEvent<T> {
    key: string;
    value: T;
}

type StateSubscriber<T> = (state: T) => void;
type changeEventSubscriber<T> = (x: StateChangeEvent<T>) => void;

export abstract class AbstractStatesScheduler<T> {
    private static readonly BASE_DELAY = 100;
    private static readonly MAX_DELAY = 15_000;

    protected pendingKeys: Set<string> = new Set();
    protected subscribers: Map<string, Set<StateSubscriber<T>>> = new Map();
    protected states: Map<string, T> = new Map();
    protected isRequestScheduled: boolean = false;
    protected changeEventSubscribers: Set<changeEventSubscriber<T>> = new Set();
    private consecutiveFailures: number = 0;

    /**
     * Execute the batch request to the API.
     * Returns an array of keys that failed to fetch.
     */
    protected abstract fetchStates(keys: string[]): Promise<string[]>;

    /**
     * Get the event name this scheduler listens to
     */
    protected abstract getChangedEventName(): string | null;

    protected constructor() {
        // Listen for state change events to update the local state
        // Using document event for asynchronous update
        if (this.getChangedEventName()) {
            window?.document.addEventListener(this.getChangedEventName(), ((
                event: CustomEvent<StateChangeEvent<T>>
            ) => {
                const { key, value } = event.detail;
                this.states.set(key, value);
                this.notifySubscribers(key, value);
                this.changeEventSubscribers.forEach((x) => x(event.detail));
            }) as EventListener);
        }
    }

    /**
     * Listen to any change events (excluding changes from data fetch / new component mounted)
     * Returns a unsubscribe function
     */
    public subscribeToChanges(callback: changeEventSubscriber<T>) {
        this.changeEventSubscribers.add(callback);
        return () => {
            this.changeEventSubscribers.delete(callback);
        };
    }

    /**
     * Register a component that needs state information
     * Returns an unregister callback function
     */
    public registerItem(itemKey: string, subscriber: StateSubscriber<T>, initialState?: T): () => void {
        const existingState = this.getState(itemKey);
        if (initialState !== undefined && existingState === undefined) {
            // If an initialState is provided and we don't already know the value, use the provided initialState
            this.pendingKeys.delete(itemKey);
            this.states.set(itemKey, initialState);
            this.notifySubscribers(itemKey, initialState);
        } else if (existingState !== undefined) {
            // If we already know the state, notify the new subscriber immediately
            subscriber(existingState);
        } else {
            this.pendingKeys.add(itemKey);
            this.scheduleRequest();
        }

        // Register the subscriber
        if (!this.subscribers.has(itemKey)) {
            this.subscribers.set(itemKey, new Set([subscriber]));
        } else {
            this.subscribers.get(itemKey).add(subscriber);
        }
        return () => {
            this.unregisterItem(itemKey, subscriber);
        };
    }

    /**
     * Unregister a component from receiving updates
     */
    private unregisterItem(itemKey: string, subscriber: StateSubscriber<T>): void {
        const subscribers = this.subscribers.get(itemKey);
        if (subscribers !== undefined) {
            subscribers.delete(subscriber);
            if (subscribers.size === 0) {
                this.subscribers.delete(itemKey);
            }
        }
    }

    /**
     * Get current state for an item if available
     */
    public getState(itemKey: string): T | undefined {
        return this.states.get(itemKey);
    }

    /**
     * Schedule a batch request to fetch states
     */
    protected scheduleRequest(): void {
        if (this.isRequestScheduled || this.pendingKeys.size === 0) {
            return;
        }

        this.isRequestScheduled = true;

        const delay = Math.min(
            // Exponential backoff for retries
            AbstractStatesScheduler.BASE_DELAY * Math.pow(4, this.consecutiveFailures),
            AbstractStatesScheduler.MAX_DELAY
        );

        // Schedule after DOM is ready and initial rendering is complete
        setTimeout(async () => {
            if (this.pendingKeys.size === 0) {
                this.isRequestScheduled = false;
                return;
            }
            const keys = Array.from(this.pendingKeys);
            this.pendingKeys.clear();

            const failedKeys = await this.fetchStates(keys);
            if (failedKeys.length > 0) {
                this.consecutiveFailures++;
                failedKeys.forEach((k) => this.pendingKeys.add(k));
            } else {
                this.consecutiveFailures = 0;
            }

            // If new keys were added during the request, schedule another one
            this.isRequestScheduled = false;
            if (this.pendingKeys.size > 0) {
                this.scheduleRequest();
            }
        }, delay);
    }

    /**
     * Notify all subscribers for a specific item key
     */
    protected notifySubscribers(key: string, state: T): void {
        if (this.subscribers.has(key)) {
            const subscribers = this.subscribers.get(key);
            subscribers.forEach((subscriber) => subscriber(state));
        }
    }
}
